import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import bankAccountService from '../../services/bankAccountService';
import { BankAccount } from '../../types';
import { toast } from 'react-hot-toast';
import BankAccountForm from './BankAccountForm';
import BankAccountDetail from './BankAccountDetail';

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

const BankAccounts: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const queryClient = useQueryClient();

  const { data: accountsData, isLoading, error } = useQuery({
    queryKey: ['bankAccounts', currentPage, searchTerm],
    queryFn: () => bankAccountService.getBankAccounts({
      page: currentPage,
      limit: 10,
      search: searchTerm || undefined
    })
  });

  const deleteMutation = useMutation({
    mutationFn: bankAccountService.deleteBankAccount,
    onSuccess: () => {
      toast.success('Bank account deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['bankAccounts'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to delete bank account');
    }
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleEdit = (account: BankAccount) => {
    setSelectedAccount(account);
    setShowForm(true);
  };

  const handleView = (account: BankAccount) => {
    setSelectedAccount(account);
    setShowDetail(true);
  };

  const handleDelete = async (account: BankAccount) => {
    if (window.confirm(`Are you sure you want to delete the account "${account.accountName}"?`)) {
      deleteMutation.mutate(account.id);
    }
  };

  const handleReconcile = (account: BankAccount) => {
    navigate(`/bank-accounts/${account.id}/reconciliation`);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedAccount(null);
    queryClient.invalidateQueries({ queryKey: ['bankAccounts'] });
  };

  if (isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: '#1c2033' }}>Bank Accounts</div>
          </div>
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{ height: 40, background: 'rgba(241,245,249,1)', borderRadius: 3 }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ background: '#ffffff', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 6, padding: 24, textAlign: 'center' }}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a' }}>
            Failed to load bank accounts. Please try again.
          </p>
        </div>
      </div>
    );
  }

  const accounts = accountsData?.accounts || [];
  const pagination = accountsData?.pagination;

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 28,
            fontWeight: 300,
            color: '#1c2033',
            letterSpacing: '0.02em',
          }}>
            Bank Accounts
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
            {pagination?.totalItems ?? accounts.length} total accounts
          </div>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: '9px 20px',
            background: 'linear-gradient(135deg, #d97706, #b45309)',
            border: 'none',
            borderRadius: 4,
            fontFamily: "'Inter', sans-serif",
            fontSize: 9,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#1c2033',
            cursor: 'pointer',
            fontWeight: 600,
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'linear-gradient(135deg, #fbbf24, #d97706)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'linear-gradient(135deg, #d97706, #b45309)')}
        >
          Add Bank Account
        </button>
      </div>

      {/* Search */}
      <form
        onSubmit={handleSearch}
        style={{
          marginBottom: 24,
          padding: 16,
          background: '#ffffff',
          border: '1px solid rgba(92,78,51,0.15)',
          borderRadius: 6,
        }}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder="Search bank accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: '#f4efe2',
              border: '1px solid rgba(92,78,51,0.25)',
              borderRadius: 4,
              fontFamily: "'Inter', sans-serif",
              fontSize: 12,
              color: '#5b5a52',
              outline: 'none',
            }}
          />
          <button
            type="submit"
            style={{
              padding: '8px 18px',
              background: 'transparent',
              border: '1px solid rgba(217,119,6,0.25)',
              borderRadius: 4,
              fontFamily: "'Inter', sans-serif",
              fontSize: 9,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: '#d97706',
              cursor: 'pointer',
            }}
          >
            Search
          </button>
        </div>
      </form>

      {/* Table */}
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
        {accounts.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <svg width="40" height="40" fill="none" stroke="rgba(217,119,6,0.25)" viewBox="0 0 24 24" style={{ margin: '0 auto 12px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>
              NO BANK ACCOUNTS FOUND
            </p>
            <button
              onClick={() => setShowForm(true)}
              style={{
                marginTop: 12,
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                color: '#d97706',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Add your first bank account
            </button>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
                    <th style={thStyle}>Account Details</th>
                    <th style={thStyle}>Bank Name</th>
                    <th style={thStyle}>Account Type</th>
                    <th style={thStyle}>Balance</th>
                    <th style={thStyle}>Status</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr
                      key={account.id}
                      style={{ transition: 'background 0.15s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={tdStyle}>
                        <div style={{ color: '#1c2033', fontWeight: 500 }}>{account.accountName}</div>
                        <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{account.accountNumber}</div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ color: '#5b5a52' }}>{account.bankName}</div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ color: '#5b5a52', textTransform: 'capitalize' }}>{account.accountType}</div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 16,
                          fontWeight: 300,
                          color: account.balance >= 0 ? '#fbbf24' : '#c95a5a',
                        }}>
                          BD {account.balance.toLocaleString('en-BH', { minimumFractionDigits: 3 })}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 10px',
                          borderRadius: 3,
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 8,
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          ...(account.isActive
                            ? { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' }
                            : { background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' }
                          ),
                        }}>
                          {account.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                          <button
                            onClick={() => handleView(account)}
                            style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleEdit(account)}
                            style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5b5a52', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleReconcile(account)}
                            style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5ac98c', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          >
                            Reconcile
                          </button>
                          <button
                            onClick={() => handleDelete(account)}
                            disabled={deleteMutation.isPending}
                            style={{
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 11,
                              color: '#c95a5a',
                              background: 'none',
                              border: 'none',
                              cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                              padding: 0,
                              opacity: deleteMutation.isPending ? 0.5 : 1,
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div style={{
                padding: '12px 20px',
                borderTop: '1px solid rgba(92,78,51,0.15)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
                  Showing {(currentPage - 1) * 10 + 1}–{Math.min(currentPage * 10, pagination.totalItems)} of {pagination.totalItems}
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '4px 12px',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 9,
                      letterSpacing: '0.1em',
                      background: 'rgba(239,232,213,0.5)',
                      color: currentPage === 1 ? '#c9c2ab' : '#8a8778',
                      border: '1px solid rgba(92,78,51,0.22)',
                      borderRadius: 3,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      opacity: currentPage === 1 ? 0.5 : 1,
                    }}
                  >
                    Previous
                  </button>
                  {[...Array(pagination.totalPages)].map((_, i) => {
                    const pageNum = i + 1;
                    const isCurrentPage = pageNum === currentPage;
                    return (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(pageNum)}
                        style={{
                          padding: '4px 10px',
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 9,
                          letterSpacing: '0.1em',
                          background: isCurrentPage ? 'linear-gradient(135deg, #d97706, #b45309)' : 'rgba(239,232,213,0.5)',
                          color: isCurrentPage ? '#1c2033' : '#8a8778',
                          border: isCurrentPage ? 'none' : '1px solid rgba(92,78,51,0.22)',
                          borderRadius: 3,
                          cursor: 'pointer',
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                    disabled={currentPage === pagination.totalPages}
                    style={{
                      padding: '4px 12px',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 9,
                      letterSpacing: '0.1em',
                      background: 'rgba(239,232,213,0.5)',
                      color: currentPage === pagination.totalPages ? '#c9c2ab' : '#8a8778',
                      border: '1px solid rgba(92,78,51,0.22)',
                      borderRadius: 3,
                      cursor: currentPage === pagination.totalPages ? 'not-allowed' : 'pointer',
                      opacity: currentPage === pagination.totalPages ? 0.5 : 1,
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <BankAccountForm
          account={selectedAccount}
          onClose={() => {
            setShowForm(false);
            setSelectedAccount(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Detail Modal */}
      {showDetail && selectedAccount && (
        <BankAccountDetail
          account={selectedAccount}
          onClose={() => {
            setShowDetail(false);
            setSelectedAccount(null);
          }}
        />
      )}
    </div>
  );
};

export default BankAccounts;
